// Pure client-side filter + sort helpers for the Management Guidelines list.
// Kept separate from React Query/Zustand so they can be unit-tested without a
// renderer. Server-side support will be revisited per FE assumption #1.

import type { ManagementGuideline } from '../types/management-guidelines.types';
import type { GuidelineSortKey, SortDir } from '../store/management-guidelines-page.store';

// PRD §3.5 — search matches GID, name, linked item, and the applied PID list
// (case-insensitive substring). Prototype joins fields with whitespace; we
// mirror that here so behavior stays identical to the static page.
export function filterGuidelines(
  rows: ManagementGuideline[],
  search: string,
): ManagementGuideline[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    const haystack = [row.gid, row.name, row.linkedItem, row.pids.join(',')]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function compareString(a: string, b: string, dir: SortDir): number {
  // localeCompare keeps Hangul + Latin ordering consistent with the prototype.
  const cmp = a.localeCompare(b);
  return dir === 'asc' ? cmp : -cmp;
}

// `deployedAt` sorts nulls (DRAFT rows) last regardless of direction so DRAFTs
// don't pollute either end of the list. Other columns use raw string compare.
export function sortGuidelines(
  rows: ManagementGuideline[],
  key: GuidelineSortKey,
  dir: SortDir,
): ManagementGuideline[] {
  const copy = rows.slice();
  copy.sort((a, b) => {
    if (key === 'deployedAt') {
      const av = a.deployedAt;
      const bv = b.deployedAt;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return compareString(av, bv, dir);
    }
    return compareString(String(a[key] ?? ''), String(b[key] ?? ''), dir);
  });
  return copy;
}
